import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BeraJobSummaryDTO } from '../hooks/types/response';
import { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import { CustomReportFilesDTO } from '@/components/ui/Inputs/types/response/customReport';
import type { BeraCommonInformations, BeraFileFilters, BeraReportsStatus, Context, Methods, State } from './types';

export type BeraJobStepsRouteParams = {
	bera_job_summary_id: string;
};

interface BeraJobStepsProviderProps {
	children: ReactNode;
}

export const BeraJobStepsContext = createContext<Context>({} as Context);

export const BeraJobStepsProvider = ({ children }: BeraJobStepsProviderProps) => {
	const params = useParams<BeraJobStepsRouteParams>();

	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(10);
	const [fileName, setFileName] = useState('');
	const [fileFilters, setFileFilters] = useState<BeraFileFilters>({});
	const [tabsLength, setTabsLength] = useState(0);
	const [allFinished, setAllFinished] = useState(false);
	const [peddingFileIds, setPeddingFileIds] = useState<string[]>([]);
	const [beraJobSummaryId, setBeraJobSummaryId] = useState('');
	const [showBeraJobResult, setShowBeraJobResult] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState<FileForCustomReport[]>([]);
	const [selectedFilesId, setSelectedFilesId] = useState<string[]>([]);
	const [beraReportsStatus, setBeraReportsStatus] = useState<BeraReportsStatus[]>([]);
	const [beraFiles, setBeraFiles] = useState<CustomReportFilesDTO>({} as CustomReportFilesDTO);
	const [beraCommonInformations, setBeraCommonInformations] = useState<BeraCommonInformations>(
		{} as BeraCommonInformations
	);
	const [beraJobSummary, setBeraJobSummary] = useState<BeraJobSummaryDTO>({} as BeraJobSummaryDTO);
	const [currentFileId, setCurrentFileId] = useState<string>('');
	const [tabsDisabled, setTabsDisabled] = useState<boolean>(false);
	const [isFileSelectionPage, setIsFileSelectionPage] = useState<boolean>(false);

	useEffect(() => {
		setPeddingFileIds(selectedFilesId);
	}, [selectedFilesId]);

	useEffect(() => {
		if (peddingFileIds.length > 1) {
			setAllFinished(false);
			peddingFileIds.forEach((fileId) => {
				handleFinishBeraReport(fileId as string, false);
			});
		}
	}, [peddingFileIds]);

	function handleFileNameChange(name: string) {
		setFileName(name);
	}

	function handleFileFiltersChange(filters: BeraFileFilters): void {
		setFileFilters(filters);
	}

	function handleSizeChange(size: number) {
		setLimit(size);
	}

	function handlePageChange(page: number) {
		setPage(page);
	}

	const handleFinishBeraReport = useCallback(
		(fileId: string, status = true) => {
			setBeraReportsStatus((prev) => {
				const copy = [...prev.filter((report) => selectedFilesId.includes(String(report?.fileId)))];
				const index = copy.findIndex((report) => report.fileId === fileId);
				if (index >= 0) {
					copy[index] = { ...copy[index], completed: status };
				}
				return copy;
			});
		},
		[selectedFilesId]
	);

	function handleShowBeraJobResultChange(show: boolean) {
		setShowBeraJobResult(show);
	}

	function handleBeraJobSummaryIdChange(id: string) {
		setBeraJobSummaryId(id);
	}

	function handleBeraFilesChange(data: CustomReportFilesDTO) {
		setBeraFiles(data);
		const beraReportsInitialStatus: BeraReportsStatus[] = defineBeraReportsStatus(data);
		setBeraReportsStatus(beraReportsInitialStatus);
	}

	function defineBeraReportsStatus(data: CustomReportFilesDTO): BeraReportsStatus[] {
		return (data?.files?.rows || []).map((file) => {
			const report = file?.bera_report?.find(
				(report) => report.bera_job_summary_id === (beraJobSummaryId || params?.bera_job_summary_id)
			);
			return {
				fileId: file?.id,
				completed: !!report?.id
			};
		});
	}

	function handleBeraJobSummaryChange(data: BeraJobSummaryDTO) {
		setBeraJobSummary(data);
	}

	function handleBeraCommonInformationsChange(data: BeraCommonInformations) {
		setBeraCommonInformations(data);
	}

	function handleSelectedFilesChange(data: FileForCustomReport[]) {
		setSelectedFiles(data);
		const filesIds = data.map((file) => String(file.id));
		setSelectedFilesId(filesIds);
		setTabsLength(data.length);
	}

	function filterSelectedFiles(selectedRows: React.Key[], customReport: CustomReportFilesDTO): FileForCustomReport[] {
		const normalizedSelectedIds = new Set((selectedRows || []).map((id) => String(id)));
		const currentPageFiles = customReport?.files?.rows || [];
		const currentPageIds = new Set(currentPageFiles.map((file) => String(file.id)));

		const previousSelectedNotInCurrentPage = selectedFiles.filter((file) => !currentPageIds.has(String(file.id)));

		const selectedFromCurrentPage = currentPageFiles.filter((file) => normalizedSelectedIds.has(String(file.id)));

		const mergedMap = new Map<string, FileForCustomReport>();
		previousSelectedNotInCurrentPage.forEach((file) => mergedMap.set(String(file.id), file));
		selectedFromCurrentPage.forEach((file) => mergedMap.set(String(file.id), file));

		return Array.from(mergedMap.values());
	}

	const removePeddingFileId = useCallback((id: string | React.Key): void => {
		setPeddingFileIds((prev) => {
			const normalizedId = String(id);
			const updated = prev.filter((fileId) => fileId !== normalizedId);
			if (updated.length === 0) {
				setAllFinished(true);
			}
			return updated;
		});
	}, []);

	const addPeddingFileId = useCallback((id: string | React.Key) => {
		setPeddingFileIds((prev) => {
			const normalizedId = String(id);
			if (!prev.includes(normalizedId)) {
				return [...prev, normalizedId];
			}
			return prev;
		});
	}, []);

	const handleCurrentFileIdChange = useCallback((id: string): void => {
		setCurrentFileId(id);
	}, []);

	function handleTabsDisabledChange(disabled: boolean): void {
		setTabsDisabled(disabled);
	}

	function handleIsFileSelectionPageChange(isFileSelectionPage: boolean): void {
		setIsFileSelectionPage(isFileSelectionPage);
	}

	const state: State = {
		page,
		limit,
		fileName,
		fileFilters,
		beraFiles,
		tabsLength,
		allFinished,
		selectedFiles,
		beraJobSummary,
		selectedFilesId,
		beraJobSummaryId,
		showBeraJobResult,
		beraReportsStatus,
		beraCommonInformations,
		peddingFileIds,
		currentFileId,
		tabsDisabled,
		isFileSelectionPage
	};

	const methods: Methods = {
		handleSizeChange,
		handlePageChange,
		filterSelectedFiles,
		handleFileNameChange,
		handleFileFiltersChange,
		handleBeraFilesChange,
		handleFinishBeraReport,
		handleSelectedFilesChange,
		handleBeraJobSummaryChange,
		handleBeraJobSummaryIdChange,
		handleShowBeraJobResultChange,
		handleBeraCommonInformationsChange,
		removePeddingFileId,
		handleCurrentFileIdChange,
		handleTabsDisabledChange,
		addPeddingFileId,
		handleIsFileSelectionPageChange
	};

	const context: Context = {
		...state,
		...methods
	};

	return <BeraJobStepsContext.Provider value={context}>{children}</BeraJobStepsContext.Provider>;
};

export function useBeraJobStepsContext() {
	const context = useContext(BeraJobStepsContext);
	return context;
}
